import { combineReducers, configureStore } from '@reduxjs/toolkit';
import i18n from 'i18next';
import {
  persistStore,
  persistReducer,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import localStorage from 'redux-persist/lib/storage';

import { analyticsReducer } from 'applications/GSP/app/providers/AnalyticsProvider/store';
import { sidebarReducer } from 'applications/GSP/layouts/MainLayout/components/Sidebar/store';
import { parentHelperReducer } from 'applications/GSP/pages/ParentHelper';
import { gspApi } from 'applications/GSP/shared/api/gsp';
import { adminReducer } from 'applications/GSP/shared/store/admin';
import { studentReducer } from 'applications/GSP/shared/store/student';
import { breadcrumbsReducer } from 'shared/store/breadcrumbs';
import { rtkQueryErrorLogger } from 'shared/store/middleware';

import { rtkQueryAuthErrorLogger, dynamicCrumbs } from './middleware';

import type { AppStore, RootState } from './types';
import type { PreloadedState } from '@reduxjs/toolkit';

export const rootReducer = combineReducers({
  sidebar: sidebarReducer,
  breadcrumbs: breadcrumbsReducer,
  student: studentReducer,
  analytics: analyticsReducer,
  parentHelper: parentHelperReducer,
  admin: adminReducer,
  [gspApi.reducerPath]: gspApi.reducer,
});

const persistConfig = {
  key: 'root',
  version: 1,
  whitelist: ['student', 'analytics', 'parentHelper'],
  storage: localStorage,
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const setupStore = (preloadedState?: PreloadedState<RootState>) =>
  configureStore({
    reducer: persistedReducer,
    preloadedState,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({
        serializableCheck: {
          isSerializable: () => true,
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      }).concat([
        gspApi.middleware,
        rtkQueryErrorLogger(
          i18n.t('GSP.pages.rootBoundary.header'),
          i18n.t('GSP.pages.rootBoundary.description'),
        ),
        rtkQueryAuthErrorLogger,
        dynamicCrumbs,
      ]),
  });

export const setupPersistStore = (store: AppStore) => persistStore(store);
